/*
 * Copyright (c) 2026. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

import { useTheme } from '@mui/material';
import type { ProjectCoordinatesEntry } from '@poweralpha/common';
import { useCallback, useEffectEvent, useMemo } from 'react';
import { useProjectsCoordinatesQuery } from '../../../project/api';
import PaCommonMap from '../../../react-components/maps/PaCommonMap';
import type { PaCommonMapHoverState, PaCommonMapProps } from '../../../react-components/maps/types';
import usePANavigate from '../../../react-components/navigate/usePANavigate';
import ProjectsMapHoverCard from './ProjectsMapHoverCard';

function getPlantTypeColor(project: ProjectCoordinatesEntry) {
  const normalized = project.plant_type?.toUpperCase() ?? '';
  if (normalized.includes('PV') && normalized.includes('S'))
    return { dark: '#0dc100', light: '#0dc000' };
  if (normalized.includes('PV')) return { dark: '#ffdd33', light: '#c5a400' };
  if (normalized.includes('S')) return { dark: '#33beff', light: '#0073a8' };
  if (normalized.includes('WIND')) return { dark: '#335cff', light: '#002ad0' };
  return { dark: '#000', light: '#000' };
}

interface Props {
  hoveredProjectId?: string | null;
  queryOptions?: Parameters<typeof useProjectsCoordinatesQuery>[0];
}

function getProjectCoordinateKey(project: ProjectCoordinatesEntry) {
  return `${project.coordinates_lat.toFixed(5)}:${project.coordinates_long.toFixed(5)}`;
}

function ProjectsMap({ hoveredProjectId, queryOptions }: Readonly<Props>) {
  const { navigate } = usePANavigate();
  const { palette } = useTheme();
  const projectCoordinatesQuery = useProjectsCoordinatesQuery(queryOptions);
  const projects = projectCoordinatesQuery.data ?? [];

  const circles = useMemo<PaCommonMapProps['circles']>(() => {
    let filteredProjects = [...projects];
    if (hoveredProjectId) {
      filteredProjects = projects.filter((project) => project.id === hoveredProjectId);
    }
    return filteredProjects.map((project) => ({
      lat: project.coordinates_lat,
      lng: project.coordinates_long,
      tooltip: project.name,
      click: () => navigate(`/projects/${project.id}/`),
      color: getPlantTypeColor(project)[palette.mode],
      hoverId: project.id,
      project,
      weight: 0.5,
    }));
  }, [hoveredProjectId, navigate, palette.mode, projects]);

  const projectsByCoordinateKey = useMemo(() => {
    const grouped = new Map<string, ProjectCoordinatesEntry[]>();
    projects.forEach((project) => {
      const key = getProjectCoordinateKey(project);
      grouped.set(key, [...(grouped.get(key) ?? []), project]);
    });
    return grouped;
  }, [projects]);

  const redirect = useEffectEvent(navigate);
  const hoverContent = useCallback(
    (hoverState: PaCommonMapHoverState) => {
      const hoveredProject = projects.find(
        (project) => project.id === hoverState.hoveredItem?.item?.hoverId,
      );
      if (!hoveredProject) return null;
      return (
        <ProjectsMapHoverCard
          projects={
            projectsByCoordinateKey.get(getProjectCoordinateKey(hoveredProject)) ?? [hoveredProject]
          }
          selectedProjectId={hoveredProject.id}
          onSelectProject={(project) => redirect(`/projects/${project.id}/`)}
        />
      );
    },
    [projects, projectsByCoordinateKey],
  );

  return (
    <PaCommonMap
      center={[38.5, -100]}
      circles={circles}
      defaultZoom={4}
      hoverContent={hoverContent}
      loading={projectCoordinatesQuery.isLoading}
      height={390}
      noLegend
      hideBnLand
    />
  );
}

export default ProjectsMap;
